import { Injectable } from '@angular/core';
import { Observable, share, finalize, tap } from 'rxjs';
import { HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class RequestDeduplicationService {
  private pendingRequests = new Map<string, Observable<HttpEvent<any>>>();

  /**
   * Generate a unique key for a request based on URL, method, and body (for POST/PUT/PATCH)
   */
  private getRequestKey(req: HttpRequest<any>): string {
    const method = req.method.toUpperCase();
    // Use urlWithParams to include query parameters for GET requests
    const url = req.urlWithParams || req.url;
    
    // For GET and DELETE requests, URL + method is sufficient
    if (method === 'GET' || method === 'DELETE') {
      return `${method}:${url}`;
    }
    
    // For POST/PUT/PATCH, include body hash to differentiate requests with same URL but different data
    let bodyKey = '';
    if (req.body) {
      if (req.body instanceof FormData) {
        // For FormData, we can't easily hash it, so we'll use URL + method only
        // This means FormData requests with same URL will be deduplicated
        bodyKey = 'formdata';
      } else if (typeof req.body === 'string') {
        bodyKey = req.body;
      } else {
        try {
          // Sort keys to ensure consistent stringification
          const sortedBody = this.sortObjectKeys(req.body);
          bodyKey = JSON.stringify(sortedBody);
        } catch {
          bodyKey = String(req.body);
        }
      }
    }
    
    return `${method}:${url}:${bodyKey}`;
  }

  /**
   * Sort object keys recursively to ensure consistent JSON stringification
   */
  private sortObjectKeys(obj: any): any {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObjectKeys(item));
    }
    
    const sorted: any = {};
    const keys = Object.keys(obj).sort();
    for (const key of keys) {
      sorted[key] = this.sortObjectKeys(obj[key]);
    }
    
    return sorted;
  }

  /**
   * Check if a request is already in progress and return the existing observable,
   * or execute the new request and cache it
   */
  deduplicateRequest<T>(
    req: HttpRequest<T>,
    executeRequest: () => Observable<HttpEvent<T>>
  ): Observable<HttpEvent<T>> {
    const requestKey = this.getRequestKey(req);

    // If request is already in progress, return the cached observable
    const existingRequest = this.pendingRequests.get(requestKey);
    if (existingRequest) {
      // Duplicate request prevented - return existing observable
      // Removed console.log to prevent browser freezing when console is open
      return existingRequest;
    }

    // Execute the request and cache it
    // Use share() instead of shareReplay to avoid caching errors
    // shareReplay caches the last value (including errors), which prevents retries
    // share() only shares the subscription while it's active, and doesn't cache results
    const request$ = executeRequest().pipe(
      tap({
        error: (error: HttpErrorResponse) => {
          // Immediately remove from cache on error
          // This must happen BEFORE share() processes the error
          // This ensures retries will make a new request instead of reusing the failed one
          this.pendingRequests.delete(requestKey);
          // Log error only if it's not a 404 (404 is expected for missing endpoints)
          if (error.status !== 404) {
            console.error(`❌ Request failed and removed from cache: ${req.method} ${req.url}`, error.status);
          }
        }
      }),
      share(), // Share the observable while it's active, but don't cache errors
      finalize(() => {
        // Remove from cache when request completes (success or error)
        // This is a safety net in case error wasn't caught in tap.error
        const stillExists = this.pendingRequests.has(requestKey);
        if (stillExists) {
          this.pendingRequests.delete(requestKey);
        }
      })
    );

    // Cache the request
    this.pendingRequests.set(requestKey, request$);

    return request$;
  }

  /**
   * Clear all pending requests (useful for testing or logout scenarios)
   */
  clearPendingRequests(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get the number of pending requests
   */
  getPendingRequestCount(): number {
    return this.pendingRequests.size;
  }
}

